import { apiRequest } from '@lib/api';

import {
    ReleaseClients,
    ReleaseComponent,
    ReleaseComponentListResponse,
} from '../schemas/release-component';
import {
    ClientsPreview,
    OperationFailedPage,
    ReleaseOperation,
    StartedOperation,
} from '../schemas/release-operation';

import type { UpdateReleaseInput } from './release-edit';

export interface CreateReleaseInput {
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly releaseVersion: string;
    readonly flagKeys: readonly string[];
    readonly reason?: string;
}

export async function listReleaseComponents(signal?: AbortSignal): Promise<ReleaseComponent[]> {
    const { releases } = await apiRequest('/release-components', ReleaseComponentListResponse, {
        signal,
    });
    return releases;
}

/** A retired or unknown id is a 404 `RELEASE_NOT_FOUND`, which the page renders as not found. */
export function getReleaseComponent(id: string, signal?: AbortSignal): Promise<ReleaseComponent> {
    return apiRequest(`/release-components/${encodeURIComponent(id)}`, ReleaseComponent, {
        signal,
    });
}

/** The clients the release is on for now. A retired or unknown id is a 404, like the detail. */
export function getReleaseClients(id: string, signal?: AbortSignal): Promise<ReleaseClients> {
    return apiRequest(`/release-components/${encodeURIComponent(id)}/clients`, ReleaseClients, {
        signal,
    });
}

export function createReleaseComponent(input: CreateReleaseInput): Promise<ReleaseComponent> {
    return apiRequest('/release-components', ReleaseComponent, { method: 'POST', body: input });
}

/** Version and flags only; see `release-edit.ts` for why the body cannot carry anything else. */
export function updateReleaseComponent(
    id: string,
    input: UpdateReleaseInput,
): Promise<ReleaseComponent> {
    return apiRequest(`/release-components/${encodeURIComponent(id)}`, ReleaseComponent, {
        method: 'PATCH',
        body: input,
    });
}

export function archiveReleaseComponent(
    id: string,
    input: { readonly expectedRowVersion: number; readonly reason?: string },
): Promise<ReleaseComponent> {
    return apiRequest(`/release-components/${encodeURIComponent(id)}/archive`, ReleaseComponent, {
        method: 'PUT',
        body: input,
    });
}

/**
 * Turn the release on or off for all clients. Answers 202 with the operation id
 * before any flag is written; the queue worker does the rest.
 */
export function startAllOperation(
    id: string,
    input: { readonly enabled: boolean; readonly reason?: string },
): Promise<StartedOperation> {
    return apiRequest(
        `/release-components/${encodeURIComponent(id)}/operations/all`,
        StartedOperation,
        { method: 'POST', body: input },
    );
}

/**
 * Turn the release on or off for the given clients (ops-csm). No cap on
 * `tenantIds`; the API writes them in target chunks. Answers 202.
 */
export function startClientsOperation(
    id: string,
    input: {
        readonly enabled: boolean;
        readonly tenantIds: readonly string[];
        readonly reason?: string;
    },
): Promise<StartedOperation> {
    return apiRequest(
        `/release-components/${encodeURIComponent(id)}/operations/clients`,
        StartedOperation,
        { method: 'POST', body: input },
    );
}

/** Turn-off preview of one chunk: 1 to 100 `tenantIds` per request. See `preview-chunks.ts`. */
export function previewClientsOperation(
    id: string,
    input: { readonly tenantIds: readonly string[] },
    signal?: AbortSignal,
): Promise<ClientsPreview> {
    return apiRequest(
        `/release-components/${encodeURIComponent(id)}/operations/clients/preview`,
        ClientsPreview,
        { method: 'POST', body: input, signal },
    );
}

/** A 404 `RELEASE_OPERATION_NOT_FOUND` means the rows expired; the page keeps the last action label. */
export function getReleaseOperation(
    id: string,
    operationId: string,
    signal?: AbortSignal,
): Promise<ReleaseOperation> {
    return apiRequest(
        `/release-components/${encodeURIComponent(id)}/operations/${encodeURIComponent(operationId)}`,
        ReleaseOperation,
        { signal },
    );
}

/** Retry is a separate route per scope, because each has its own role tier. */
export function retryPath(id: string, operationId: string, scope: 'all' | 'clients'): string {
    return `/release-components/${encodeURIComponent(id)}/operations/${scope}/${encodeURIComponent(operationId)}/retry`;
}

export function failedItemsPath(id: string, operationId: string, cursor?: string): string {
    const base = `/release-components/${encodeURIComponent(id)}/operations/${encodeURIComponent(operationId)}?status=failed`;
    return cursor === undefined ? base : `${base}&cursor=${encodeURIComponent(cursor)}`;
}

/**
 * Re-enqueue the pending and failed items of the latest operation. Answers 202;
 * 409 `RELEASE_OPERATION_NOT_RETRYABLE`, 404 `RELEASE_OPERATION_NOT_FOUND`, 503
 * `RELEASE_OPERATION_ENQUEUE_INCOMPLETE`.
 */
export function retryOperation(
    id: string,
    operationId: string,
    scope: 'all' | 'clients',
): Promise<StartedOperation> {
    return apiRequest(retryPath(id, operationId, scope), StartedOperation, { method: 'POST' });
}

export function getFailedItems(
    id: string,
    operationId: string,
    cursor?: string,
    signal?: AbortSignal,
): Promise<OperationFailedPage> {
    return apiRequest(failedItemsPath(id, operationId, cursor), OperationFailedPage, { signal });
}
